import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import StatusBadge from "@/components/StatusBadge";
import ITPGridEditor from "@/components/itp/ITPGridEditor";
import GridPropertiesPanel from "@/components/itp/GridPropertiesPanel";
import AddTableDialog from "@/components/itp/AddTableDialog";
import PointTypesDialog from "@/components/itp/PointTypesDialog";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowUp, ArrowDown, Trash2, Copy, Save, Eye, EyeOff,
  ShieldCheck, History, Loader2, GitBranch, Grid3X3, Heading1, PanelTop,
  Type, AlignLeft, Calendar, ChevronDownSquare, CheckSquare, Activity,
  Camera, MessageSquare, PenLine,
} from "lucide-react";

const TYPE_LABELS = {
  heading: "Heading", text: "Text", long_text: "Long Text", date: "Date",
  dropdown: "Dropdown", checkbox: "Checkbox", status: "Status",
  evidence: "Evidence", signature: "Signature",
  instruction: "Notes", static_text: "Text block", divider: "Divider", page_break: "Page break",
  number: "Number", photo: "Photo", attachment: "Attachment",
};
const INPUT_TYPES = new Set(["text", "long_text", "number", "date", "dropdown", "checkbox", "status", "photo", "attachment", "evidence", "signature"]);
const SIG_LABELS = { contractor: "Contractor Signature", client: "Client Signature", superintendent: "Superintendent Signature" };

// Simple field palette — plain-English, construction-ITP specific.
const FIELDS = [
  { key: "heading", label: "Heading", icon: Heading1, make: () => ({ type: "heading", config: { label: "Heading", text: "Heading" } }) },
  { key: "section", label: "Section Heading", icon: PanelTop, action: "add_section" },
  { key: "text", label: "Text", icon: Type, make: () => ({ type: "text", config: { label: "Text" } }) },
  { key: "long_text", label: "Long Text", icon: AlignLeft, make: () => ({ type: "long_text", config: { label: "Long Text" } }) },
  { key: "date", label: "Date", icon: Calendar, make: () => ({ type: "date", config: { label: "Date" } }) },
  { key: "dropdown", label: "Dropdown", icon: ChevronDownSquare, make: () => ({ type: "dropdown", config: { label: "Dropdown", options: ["Option 1", "Option 2"] } }) },
  { key: "checkbox", label: "Checkbox", icon: CheckSquare, make: () => ({ type: "checkbox", config: { label: "Checkbox" } }) },
  { key: "status", label: "Status", icon: Activity, make: () => ({ type: "status", config: { label: "Status" } }) },
  { key: "evidence", label: "Evidence", icon: Camera, make: () => ({ type: "evidence", config: { label: "Evidence" } }) },
  { key: "comments", label: "Comments", icon: MessageSquare, make: () => ({ type: "long_text", config: { label: "Comments" } }) },
  { key: "table", label: "ITP Table", icon: Grid3X3, action: "add_table" },
  { key: "sig_contractor", label: "Contractor Signature", icon: PenLine, make: () => ({ type: "signature", config: { label: "Contractor Signature", signing_role: "contractor" } }) },
  { key: "sig_client", label: "Client Signature", icon: PenLine, make: () => ({ type: "signature", config: { label: "Client Signature", signing_role: "client" } }) },
  { key: "sig_superintendent", label: "Superintendent Signature", icon: PenLine, make: () => ({ type: "signature", config: { label: "Superintendent Signature", signing_role: "superintendent" } }) },
];

export default function TemplateBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [version, setVersion] = useState(null);
  const [schema, setSchema] = useState(null);
  const [selected, setSelected] = useState(null); // {sectionId, componentId}
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addTableOpen, setAddTableOpen] = useState(false);
  const [ptDialogOpen, setPtDialogOpen] = useState(false);
  const [pointTypes, setPointTypes] = useState([]);
  const [gridSel, setGridSel] = useState(null); // {rowIdx,cellIdx}|{colIdx} within selected table
  const [name, setName] = useState("");

  const editable = version && ["draft", "under_review"].includes(version.status);

  const load = useCallback(async () => {
    const r = await api.get(`/v2/itp-templates/${id}`);
    setTpl(r.data);
    setName(r.data.name);
    const cv = r.data.draft_version || r.data.current_version;
    setVersion(cv);
    setSchema(cv?.schema || { sections: [], field_anchors: [], table_defs: [] });
    setDirty(false);
  }, [id]);
  useEffect(() => { load().catch(() => toast.error("Template not found")); }, [load]);

  const loadPointTypes = useCallback(() => {
    api.get("/v2/itp-point-types").then((r) => setPointTypes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadPointTypes(); }, [loadPointTypes]);

  const mutate = (fn) => setSchema(s => { const c = structuredClone(s); fn(c); setDirty(true); return c; });

  // Targeted immutable update for table grids — avoids re-cloning the entire schema
  const updateComponentConfig = useCallback((sectionId, componentId, newConfig) => {
    setSchema(s => ({
      ...s,
      sections: s.sections.map(sec => sec.id !== sectionId ? sec : {
        ...sec,
        components: sec.components.map(c => c.id !== componentId ? c : { ...c, config: newConfig }),
      }),
    }));
    setDirty(true);
  }, []);

  const insertComponent = (comp) => {
    let targetId = selected?.sectionId && schema.sections.some(x => x.id === selected.sectionId)
      ? selected.sectionId : schema.sections[schema.sections.length - 1]?.id;
    const newSecId = targetId ? null : crypto.randomUUID();
    if (!targetId) targetId = newSecId;
    mutate(s => {
      let target = s.sections.find(x => x.id === targetId);
      if (!target) { target = { id: newSecId, title: "Section 1", components: [] }; s.sections.push(target); }
      target.components.push(comp);
    });
    setSelected({ sectionId: targetId, componentId: comp.id });
    setGridSel(null);
  };

  const onPaletteClick = (f) => {
    if (!editable) return;
    if (f.action === "add_section") {
      mutate(s => s.sections.push({ id: crypto.randomUUID(), title: `Section ${s.sections.length + 1}`, components: [] }));
      return;
    }
    if (f.action === "add_table") { setAddTableOpen(true); return; }
    const { type, config } = f.make();
    insertComponent({ id: crypto.randomUUID(), component_type: type, config, validation: { required: false }, workflow: {} });
  };

  const addTableComponent = (config) => {
    insertComponent({ id: crypto.randomUUID(), component_type: "table", config, validation: {}, workflow: {} });
    setAddTableOpen(false);
  };

  const compOp = (sectionId, componentId, op) => mutate(s => {
    const sec = s.sections.find(x => x.id === sectionId);
    const idx = sec.components.findIndex(c => c.id === componentId);
    if (op === "up" && idx > 0) [sec.components[idx - 1], sec.components[idx]] = [sec.components[idx], sec.components[idx - 1]];
    if (op === "down" && idx < sec.components.length - 1) [sec.components[idx + 1], sec.components[idx]] = [sec.components[idx], sec.components[idx + 1]];
    if (op === "delete") { sec.components.splice(idx, 1); setSelected(null); }
    if (op === "duplicate") {
      const copy = structuredClone(sec.components[idx]);
      copy.id = crypto.randomUUID();
      sec.components.splice(idx + 1, 0, copy);
    }
  });

  const updateSelected = (patch, bucket = "config") => {
    if (!selected) return;
    mutate(s => {
      const sec = s.sections.find(x => x.id === selected.sectionId);
      const c = sec?.components.find(x => x.id === selected.componentId);
      if (c) c[bucket] = { ...c[bucket], ...patch };
    });
  };

  const selectedComp = useMemo(() => {
    if (!selected || !schema) return null;
    const sec = schema.sections.find(x => x.id === selected.sectionId);
    return sec?.components.find(x => x.id === selected.componentId) || null;
  }, [selected, schema]);

  const saveName = async () => {
    const clean = name.trim();
    if (!clean || clean === tpl.name) { setName(tpl.name); return; }
    try {
      await api.put(`/v2/itp-templates/${id}`, { name: clean });
      setTpl(t => ({ ...t, name: clean }));
      toast.success("Template renamed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
      setName(tpl.name);
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put(`/v2/itp-templates/${id}/draft-schema`, { schema_data: schema });
      setDirty(false);
      toast.success("Draft saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const lifecycle = async (action, label) => {
    if (dirty && editable) await saveDraft();
    setBusy(true);
    try {
      await api.post(`/v2/itp-templates/${id}/${action}`);
      toast.success(label);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const openVersion = async (vid) => {
    const r = await api.get(`/v2/itp-templates/${id}/versions/${vid}`);
    setVersion(r.data);
    setSchema(r.data.schema || { sections: [] });
    setSelected(null);
    setGridSel(null);
    setDirty(false);
    setHistoryOpen(false);
  };

  if (!tpl || !schema) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-4 md:p-6" data-testid="template-builder-page">
      {/* Top toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav("/itp")} data-testid="builder-back-btn"><ArrowLeft className="size-4"/></Button>
        <div className="min-w-64">
          <div className="overline text-teal-700">ITP Template Builder</div>
          <div className="flex items-center gap-2">
            <Input
              value={name}
              disabled={tpl.status === "Archived"}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
              className="h-8 w-64 border-transparent font-heading text-lg font-bold tracking-tight hover:border-slate-200 focus:border-slate-300"
              data-testid="builder-template-name"
            />
            <span className="font-mono text-xs text-slate-500">Rev {version?.revision}</span>
            <StatusBadge status={tpl.status}/>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)} data-testid="builder-history-btn">
            <History className="mr-1.5 size-4"/> History
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPreview(p => !p)} data-testid="builder-preview-btn">
            {preview ? <EyeOff className="mr-1.5 size-4"/> : <Eye className="mr-1.5 size-4"/>}{preview ? "Back to editing" : "Preview"}
          </Button>
          {editable && (
            <Button size="sm" onClick={saveDraft} disabled={saving || !dirty} className="bg-teal-700 hover:bg-teal-800" data-testid="builder-save-btn">
              {saving ? <Loader2 className="mr-1.5 size-4 animate-spin"/> : <Save className="mr-1.5 size-4"/>}
              {dirty ? "Save Draft" : "Saved"}
            </Button>
          )}
          {["draft", "under_review"].includes(version?.status || "") && (
            <Button size="sm" onClick={() => lifecycle("publish", "ITP Template published.")} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="builder-publish-btn">
              <ShieldCheck className="mr-1.5 size-4"/> Publish
            </Button>
          )}
          {tpl.status === "Published" && !editable && (
            <Button size="sm" variant="outline" onClick={() => lifecycle("new-revision", "New draft revision created.")} disabled={busy} data-testid="builder-new-revision-btn">
              <GitBranch className="mr-1.5 size-4"/> New Revision
            </Button>
          )}
        </div>
      </div>

      {!editable && !preview && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="builder-readonly-banner">
          This revision is published and cannot be edited. Use <b>New Revision</b> to make changes.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[210px_1fr_290px]">
        {/* LEFT — fields */}
        {!preview && (
          <Card className="h-fit border-slate-200 p-3" data-testid="component-palette">
            <div className="overline mb-2 text-teal-700">Fields</div>
            <div className="grid grid-cols-1 gap-1.5">
              {FIELDS.map((f) => (
                <button
                  key={f.key}
                  disabled={!editable}
                  onClick={() => onPaletteClick(f)}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs font-medium disabled:opacity-40 ${f.key === "table" ? "border-teal-600 bg-teal-50 font-bold text-teal-800 hover:bg-teal-100" : "border-slate-200 text-slate-700 hover:border-teal-600 hover:bg-teal-50"}`}
                  data-testid={`palette-${f.key}`}
                >
                  <f.icon className="size-3.5 shrink-0"/>{f.label}
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* CENTRE — template */}
        <div className={preview ? "lg:col-span-3" : ""}>
          <Card className="border-slate-200 p-4" data-testid="builder-canvas">
            {schema.sections.length === 0 && <div className="py-10 text-center text-sm text-slate-500">Add fields from the left panel to start building your ITP.</div>}
            {schema.sections.map(sec => (
              <div key={sec.id} className="mb-5">
                {preview ? (
                  sec.title?.trim() && <h3 className="mb-2 border-b border-slate-200 pb-1 font-heading text-lg font-bold">{sec.title}</h3>
                ) : (
                  <div className="mb-2 flex items-center gap-2">
                    <Input
                      value={sec.title}
                      disabled={!editable}
                      onChange={(e) => mutate(s => { s.sections.find(x => x.id === sec.id).title = e.target.value; })}
                      className="h-8 max-w-md font-semibold"
                      data-testid={`section-title-${sec.id}`}
                    />
                    <Button variant="ghost" size="sm" disabled={!editable} className="text-rose-600"
                      onClick={() => mutate(s => { s.sections = s.sections.filter(x => x.id !== sec.id); })}
                      data-testid={`delete-section-${sec.id}`}>
                      <Trash2 className="size-4"/>
                    </Button>
                  </div>
                )}
                <div className="space-y-2">
                  {sec.components.map((c, ci) => (
                    <div
                      key={c.id}
                      onClick={() => { if (!preview) { setSelected({ sectionId: sec.id, componentId: c.id }); if (selected?.componentId !== c.id) setGridSel(null); } }}
                      className={`rounded-md border p-2.5 ${!preview ? "cursor-pointer" : "border-transparent"} ${selected?.componentId === c.id && !preview ? "border-teal-600 ring-1 ring-teal-600" : !preview ? "border-slate-200" : ""}`}
                      data-testid={`canvas-comp-${c.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          {c.component_type === "table" ? (
                            <div>
                              {(c.config.label || "").trim() && <div className="mb-1 text-xs font-bold text-slate-700">{c.config.label}</div>}
                              <ITPGridEditor
                                config={c.config}
                                editable={editable && !preview && selected?.componentId === c.id}
                                compact={preview}
                                sel={selected?.componentId === c.id ? gridSel : null}
                                onSel={(g) => { if (preview) return; setGridSel(g); setSelected({ sectionId: sec.id, componentId: c.id }); }}
                                onChange={(cfg) => updateComponentConfig(sec.id, c.id, cfg)}
                              />
                              {!preview && editable && selected?.componentId !== c.id && (
                                <div className="mt-1 text-[10px] text-slate-400">Click a cell to edit this table.</div>
                              )}
                            </div>
                          ) : (
                            <ComponentRender comp={c} preview={preview}/>
                          )}
                        </div>
                        {!preview && editable && (
                          <div className="flex shrink-0 items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-6 px-1" disabled={ci === 0} onClick={() => compOp(sec.id, c.id, "up")}><ArrowUp className="size-3.5"/></Button>
                            <Button variant="ghost" size="sm" className="h-6 px-1" disabled={ci === sec.components.length - 1} onClick={() => compOp(sec.id, c.id, "down")}><ArrowDown className="size-3.5"/></Button>
                            <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => compOp(sec.id, c.id, "duplicate")}><Copy className="size-3.5"/></Button>
                            <Button variant="ghost" size="sm" className="h-6 px-1 text-rose-600" onClick={() => compOp(sec.id, c.id, "delete")} data-testid={`delete-comp-${c.id}`}><Trash2 className="size-3.5"/></Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {sec.components.length === 0 && !preview && <div className="rounded-md border border-dashed border-slate-300 p-3 text-center text-xs text-slate-400">Empty section</div>}
                </div>
              </div>
            ))}
          </Card>
        </div>

        {/* RIGHT — simple settings */}
        {!preview && (
          <Card className="h-fit border-slate-200 p-3" data-testid="properties-panel">
            <div className="overline mb-2 text-teal-700">Settings</div>
            {!selectedComp && <div className="text-xs text-slate-500">Select something on the template to edit its settings.</div>}
            {selectedComp && selectedComp.component_type === "table" ? (
              <div className="space-y-3">
                <div>
                  <Label>Table title</Label>
                  <Input className="mt-1 h-8" disabled={!editable} value={selectedComp.config.label || ""}
                    onChange={(e) => updateComponentConfig(selected.sectionId, selected.componentId, { ...selectedComp.config, label: e.target.value })}
                    data-testid="table-prop-label"/>
                </div>
                <GridPropertiesPanel
                  config={selectedComp.config}
                  sel={gridSel}
                  editable={editable}
                  pointTypes={pointTypes}
                  onManagePointTypes={() => setPtDialogOpen(true)}
                  onChange={(cfg) => updateComponentConfig(selected.sectionId, selected.componentId, cfg)}
                />
              </div>
            ) : selectedComp && (
              <div className="space-y-3">
                <div className="text-xs font-semibold text-slate-500">
                  {selectedComp.component_type === "signature"
                    ? SIG_LABELS[selectedComp.config.signing_role] || "Signature"
                    : TYPE_LABELS[selectedComp.component_type] || selectedComp.component_type}
                </div>
                {["heading", "instruction", "static_text"].includes(selectedComp.component_type) ? (
                  <div>
                    <Label>Text</Label>
                    <Textarea rows={3} className="mt-1" disabled={!editable} value={selectedComp.config.text || selectedComp.config.label || ""}
                      onChange={(e) => updateSelected({ text: e.target.value, label: e.target.value })} data-testid="prop-text"/>
                  </div>
                ) : (
                  <div>
                    <Label>Label</Label>
                    <Input className="mt-1" disabled={!editable} value={selectedComp.config.label || ""}
                      onChange={(e) => updateSelected({ label: e.target.value })} data-testid="prop-label"/>
                  </div>
                )}
                {selectedComp.component_type === "dropdown" && (
                  <div>
                    <Label>Options (one per line)</Label>
                    <Textarea rows={4} className="mt-1" disabled={!editable} value={(selectedComp.config.options || []).join("\n")}
                      onChange={(e) => updateSelected({ options: e.target.value.split("\n") })} data-testid="prop-options"/>
                  </div>
                )}
                {INPUT_TYPES.has(selectedComp.component_type) && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox disabled={!editable} checked={!!selectedComp.validation?.required}
                      onCheckedChange={(v) => updateSelected({ required: !!v }, "validation")} data-testid="prop-required"/>
                    Required
                  </label>
                )}
              </div>
            )}
          </Card>
        )}
      </div>

      <AddTableDialog open={addTableOpen} onOpenChange={setAddTableOpen} onCreate={addTableComponent}/>
      <PointTypesDialog open={ptDialogOpen} onOpenChange={setPtDialogOpen} onChanged={loadPointTypes}/>

      {/* Revision history */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent className="w-full sm:max-w-md" data-testid="revision-history-panel">
          <SheetHeader>
            <SheetTitle className="font-heading">Revision History</SheetTitle>
            <SheetDescription>All revisions of this template. Published revisions cannot be changed.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {(tpl.versions || []).map(v => (
              <button key={v.id} onClick={() => openVersion(v.id)}
                className="flex w-full items-center justify-between rounded-md border border-slate-200 p-2.5 text-left text-sm hover:border-teal-600"
                data-testid={`revision-row-${v.revision}`}>
                <div>
                  <div className="font-semibold">Revision {v.revision}</div>
                  <div className="text-xs text-slate-500">{(v.published_at || v.created_at || "").slice(0, 16).replace("T", " ")}{v.change_note ? ` · ${v.change_note.slice(0, 40)}` : ""}</div>
                </div>
                <span className="text-xs font-semibold capitalize text-slate-600">{v.status}</span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ComponentRender({ comp, preview }) {
  const t = comp.component_type;
  const label = comp.config.label || "";
  const req = comp.validation?.required;
  const Tag = ({ children }) => (
    <div>
      <div className="text-xs font-semibold text-slate-600">{label}{req && <span className="text-rose-600"> *</span>}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
  if (t === "heading") return <div className="font-heading text-base font-bold">{comp.config.text || label}</div>;
  if (t === "instruction") return <div className="text-xs italic text-slate-500">{comp.config.text || label}</div>;
  if (t === "static_text") return <div className="text-sm text-slate-700">{comp.config.text || label}</div>;
  if (t === "divider") return <hr className="border-slate-300"/>;
  if (t === "page_break") return <div className="border-t-2 border-dashed border-slate-300 text-center text-[10px] uppercase text-slate-400">page break</div>;
  if (t === "long_text") return <Tag><Textarea rows={2} disabled placeholder={comp.config.placeholder || "Long text"}/></Tag>;
  if (t === "dropdown") return <Tag><div className="rounded-md border border-slate-200 px-2 py-1.5 text-xs text-slate-400">{(comp.config.options || []).filter(Boolean).join(" / ") || "Dropdown"}</div></Tag>;
  if (t === "checkbox") return <label className="flex items-center gap-2 text-sm"><Checkbox disabled/> {label}{req && <span className="text-rose-600">*</span>}</label>;
  if (t === "status") return <Tag><div className="inline-flex rounded-full border border-slate-200 px-2.5 py-0.5 text-xs text-slate-400">Status</div></Tag>;
  if (t === "photo" || t === "attachment" || t === "evidence") return <Tag><div className="rounded-md border border-dashed border-slate-300 p-2 text-center text-xs text-slate-400">{t === "attachment" ? "Attachment upload" : "Evidence / photo upload"}</div></Tag>;
  if (t === "signature") {
    const roleLabel = SIG_LABELS[comp.config.signing_role] || "Signature";
    return <Tag><div className="rounded-md border border-dashed border-amber-300 bg-amber-50/50 p-2 text-center text-xs text-amber-700">[ {roleLabel} ]</div></Tag>;
  }
  return <Tag><Input disabled placeholder={comp.config.placeholder || TYPE_LABELS[t] || t}/></Tag>;
}
